import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import GigCard from "@/components/GigCard";

export default function GigBrowseSection({
  filters,
  cityOptions,
  isUsingFallback,
  displayedGigs,
  isLoading,
  activeTab,
  canViewAvailability,
  canManageAvailability,
  myAvailabilityGigs,
  interestedIds,
  pendingGigId,
  isVenueUser,
  currentUserId,
  onFilterChange,
  onResetFilters,
  onTabChange,
  onViewDetails,
  onToggleAvailability,
  onOpenInterestedModal,
  onEditGig,
  onDeleteGig,
  onRequestAuth,
}) {
  const [filtersOpen, setFiltersOpen] = useState(false);

  return (
    <div id="browse" className="space-y-4">
      <div className="flex items-center lg:hidden">
        <button
          type="button"
          className="btn-base btn-secondary text-sm px-4 py-1.5 flex items-center gap-2"
          onClick={() => setFiltersOpen((f) => !f)}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
            <path d="M1 3h12M3 7h8M5 11h4"/>
          </svg>
          {filtersOpen ? "Hide filters" : "Filters"}
        </button>
      </div>
    <div className="grid gap-6 lg:grid-cols-[minmax(0,280px)_minmax(0,1fr)]">
      <aside className={`${filtersOpen ? "block" : "hidden"} lg:block space-y-4 lg:sticky lg:top-24`}>
        <div className="panel-strong p-5">
          <h2 className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-400 dark:text-zinc-500">
            Refine results
          </h2>
          <div className="mt-4 space-y-4">
            <div>
              <label
                className="block text-xs font-medium uppercase tracking-[0.08em] text-zinc-400 dark:text-zinc-500 mb-1"
                htmlFor="gig-search"
              >
                Search
              </label>
              <Input
                id="gig-search"
                value={filters.search}
                onChange={onFilterChange("search")}
                placeholder="Search by title"
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label
                  className="block text-xs font-medium uppercase tracking-[0.08em] text-zinc-400 dark:text-zinc-500 mb-1"
                  htmlFor="gig-date-from"
                >
                  From
                </label>
                <Input
                  id="gig-date-from"
                  type="date"
                  value={filters.dateFrom}
                  onChange={onFilterChange("dateFrom")}
                />
              </div>
              <div>
                <label
                  className="block text-xs font-medium uppercase tracking-[0.08em] text-zinc-400 dark:text-zinc-500 mb-1"
                  htmlFor="gig-date-to"
                >
                  To
                </label>
                <Input
                  id="gig-date-to"
                  type="date"
                  value={filters.dateTo}
                  onChange={onFilterChange("dateTo")}
                />
              </div>
            </div>
            <div>
              <label
                className="block text-xs font-medium uppercase tracking-[0.08em] text-zinc-400 dark:text-zinc-500 mb-1"
                htmlFor="gig-min-payout"
              >
                Minimum payout (DKK)
              </label>
              <Input
                id="gig-min-payout"
                type="number"
                min="0"
                placeholder="e.g. 2000"
                value={filters.minPayout}
                onChange={onFilterChange("minPayout")}
              />
            </div>
            <div>
              <label
                className="block text-xs font-medium uppercase tracking-[0.08em] text-zinc-400 dark:text-zinc-500 mb-1"
                htmlFor="gig-city-filter"
              >
                City
              </label>
              <select
                id="gig-city-filter"
                value={filters.city}
                onChange={onFilterChange("city")}
                className="w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/30 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100 dark:[color-scheme:dark]"
              >
                <option value="all">All cities</option>
                {cityOptions.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                className="block text-xs font-medium uppercase tracking-[0.08em] text-zinc-400 dark:text-zinc-500 mb-1"
                htmlFor="gig-sort"
              >
                Sort by
              </label>
              <select
                id="gig-sort"
                value={filters.sort}
                onChange={onFilterChange("sort")}
                className="w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/30 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100 dark:[color-scheme:dark]"
              >
                <option value="date_asc">Soonest first</option>
                <option value="date_desc">Latest first</option>
                <option value="payout_desc">Highest payout</option>
                <option value="payout_asc">Lowest payout</option>
              </select>
            </div>
            <Button
              type="button"
              className="w-full btn-secondary"
              onClick={onResetFilters}
            >
              Clear filters
            </Button>
          </div>
        </div>
        {isUsingFallback && (
          <div className="rounded-lg border border-zinc-200/60 bg-zinc-50 p-3 text-xs text-zinc-500 dark:border-white/[0.06] dark:bg-white/[0.03] dark:text-zinc-400">
            Showing demo gigs while the real data loads.
          </div>
        )}
      </aside>
      <section className="panel-strong p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex items-center gap-0.5 rounded-lg border border-zinc-200/70 bg-white/80 p-0.5 text-sm dark:border-white/[0.07] dark:bg-white/[0.04]">
            <button
              type="button"
              onClick={() => onTabChange("all")}
              className={`rounded-md px-4 py-1.5 text-sm font-medium transition ${
                activeTab === "all"
                  ? "bg-white text-zinc-900 shadow-sm dark:bg-white/[0.08] dark:text-zinc-100"
                  : "text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"
              }`}
            >
              All gigs
            </button>
            {canViewAvailability && (
              <button
                type="button"
                onClick={() => onTabChange("mine")}
                className={`rounded-md px-4 py-1.5 text-sm font-medium transition ${
                  activeTab === "mine"
                    ? "bg-white text-zinc-900 shadow-sm dark:bg-white/[0.08] dark:text-zinc-100"
                    : "text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"
                }`}
              >
                My availability
              </button>
            )}
          </div>
          <span className="text-xs text-zinc-400 dark:text-zinc-500">
            {activeTab === "mine" && canViewAvailability
              ? `${myAvailabilityGigs.length} saved`
              : `${displayedGigs.length} results`}
          </span>
        </div>
        {!canManageAvailability && !isUsingFallback && (
          <p className="mt-3 text-xs text-zinc-400 dark:text-zinc-500">
            <button
              type="button"
              className="text-amber-500 hover:text-amber-400 underline underline-offset-2 transition-colors"
              onClick={onRequestAuth}
            >
              Log in as an artist
            </button>
            {" "}to mark the gigs you&apos;re available for.
          </p>
        )}
        <div className="mt-5">
          {isLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={`skeleton-${index}`} className="rounded-xl border border-zinc-100 dark:border-white/[0.06] p-5">
                  <div className="flex animate-pulse flex-col gap-3">
                    <div className="h-4 w-3/4 rounded-md bg-zinc-200 dark:bg-white/[0.08]"></div>
                    <div className="h-3 w-1/2 rounded-md bg-zinc-100 dark:bg-white/[0.05]"></div>
                    <div className="h-3 w-full rounded-md bg-zinc-100 dark:bg-white/[0.05]"></div>
                    <div className="mt-2 h-9 w-full rounded-lg bg-zinc-200 dark:bg-white/[0.08]"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : displayedGigs.length === 0 ? (
            <p className="text-sm text-zinc-500 dark:text-zinc-400">
              {activeTab === "mine"
                ? "You haven't marked any gigs yet."
                : "No gigs match your filters right now."}
            </p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {displayedGigs.map((gig) => (
                <GigCard
                  key={gig.id}
                  gig={gig}
                  isInterested={interestedIds.has(String(gig.id))}
                  isPending={pendingGigId === gig.id}
                  canManageAvailability={canManageAvailability}
                  isUsingFallback={isUsingFallback}
                  isVenueUser={isVenueUser}
                  isOwnGig={isVenueUser && String(gig.venue_id) === String(currentUserId)}
                  onViewDetails={onViewDetails}
                  onToggleAvailability={onToggleAvailability}
                  onOpenInterestedModal={onOpenInterestedModal}
                  onEditGig={onEditGig}
                  onDeleteGig={onDeleteGig}
                  onRequestAuth={onRequestAuth}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
    </div>
  );
}
